import { memorialsDesktop, memorialsMobile, FULL_MEMORIAL_URL, sectionConfig } from '../data/memorials';
import novaFestivalVideo from '../videos/nova_festival.mp4';

/**
 * Service to handle memorial data and operations
 * with memoization for better performance
 */
class MemorialService {
  constructor() {
    // Cache for memoization
    this._memorialCache = {
      desktop: null,
      mobile: null,
      byId: {}
    };
  }

  /**
   * Get all memorial data for the specified device type
   * with memoization for better performance
   * @param {boolean} isMobile - Whether to return mobile or desktop data
   * @returns {Array} Array of memorial data objects
   */
  getMemorials(isMobile) {
    const cacheKey = isMobile ? 'mobile' : 'desktop';
    
    // Check if we have cached data
    if (this._memorialCache[cacheKey]) {
      return this._memorialCache[cacheKey];
    }
    
    // Get and cache data
    const memorials = isMobile ? memorialsMobile : memorialsDesktop;
    this._memorialCache[cacheKey] = memorials;
    
    return memorials;
  }

  /**
   * Get a single memorial by ID with memoization
   * @param {number} id - The memorial ID to retrieve
   * @param {boolean} isMobile - Whether to use mobile or desktop data
   * @returns {Object|null} Memorial object or null if not found
   */
  getMemorialById(id, isMobile) {
    const cacheKey = `${id}-${isMobile ? 'mobile' : 'desktop'}`;
    
    // Check cache first
    if (this._memorialCache.byId[cacheKey]) {
      return this._memorialCache.byId[cacheKey];
    }
    
    // Look up and cache result
    const memorials = this.getMemorials(isMobile);
    const memorial = memorials.find(memorial => memorial.id === id) || null;
    
    // Cache the result (even if null)
    this._memorialCache.byId[cacheKey] = memorial;
    
    return memorial;
  }

  /**
   * Get the URL for the full memorial site
   * @returns {string} The full memorial URL
   */
  getFullMemorialUrl() {
    return FULL_MEMORIAL_URL;
  }

  /**
   * Get the URL for the Nova festival video
   * @returns {string} The video URL
   */
  getNovaFestivalVideo() {
    return novaFestivalVideo;
  }

  /**
   * Determines if a memorial content requires special handling
   * @param {Object} memorial - The memorial object
   * @returns {boolean} True if the memorial requires special rendering
   */
  isSpecialMemorial(memorial) {
    return memorial && memorial.name === 'Full Memorial';
  }

  /**
   * Get section configuration (which IDs belong to which section)
   * @returns {Object} Section configuration object
   */
  getSectionConfig() {
    return sectionConfig;
  }

  /**
   * Get sections as array for UI rendering
   * @returns {Array} Array of section objects with id and title
   */
  getSections() {
    return Object.entries(sectionConfig).map(([id, config]) => ({
      id,
      title: config.title,
      entryIds: config.entryIds
    }));
  }
  
  /**
   * Clear the memorial cache to free memory
   * Call this when the component unmounts if needed
   */
  clearCache() {
    this._memorialCache = {
      desktop: null,
      mobile: null,
      byId: {}
    };
  }
}

export default new MemorialService();